@if (isLoading) {
  <div class="bench-detail__loading">
    <mat-spinner diameter="40"></mat-spinner>
  </div>
} @else if (entry) {
  <div class="bench-detail">

    <!-- Top action bar -->
    <div class="bench-detail__actionbar">
      <button mat-button class="bench-detail__back" (click)="backToList()">
        <mat-icon>chevron_left</mat-icon>
        {{ 'benchDetail.backToList' | translate }}
      </button>
      <span class="bench-detail__actionbar-spacer"></span>
      @if (showSaved && (savedArea === 'all' || !isDirty)) {
        <span class="bench-detail__saved">
          <mat-icon>check</mat-icon>{{ 'benchDetail.saved' | translate }}
        </span>
      } @else if (isDirty) {
        <span class="bench-detail__dirty">● {{ 'benchDetail.unsaved' | translate }}</span>
      }
      @if (hasBusyOffer) {
        <span class="bench-detail__save-blocked">
          {{ 'benchDetail.offers.attachment.saveBlocked' | translate }}
        </span>
      }
      @if (hasOfferWithoutProbability) {
        <span class="bench-detail__save-blocked">
          {{ 'benchDetail.offers.probabilityMissingSaveBlocked' | translate }}
        </span>
      }
      @if (hasIncompleteOffer) {
        <span class="bench-detail__save-blocked">
          {{ 'benchDetail.offers.incompleteSaveBlocked' | translate }}
        </span>
      }
      <span class="bench-detail__save-tooltip"
            [matTooltip]="'benchDetail.saveAllHint' | translate" [matTooltipDisabled]="!isSaveAllDisabled">
        <button mat-flat-button class="bench-detail__save"
                [disabled]="isSaveAllDisabled"
                [matTooltip]="'benchDetail.saveAllHint' | translate" [matTooltipDisabled]="isSaveAllDisabled"
                (click)="save()">
          {{ 'benchDetail.saveAll' | translate }}
        </button>
      </span>
    </div>

    <!-- Hero header (Detail-Kopf, NP20-795: alle Stammdaten + Sprung-Anker) -->
    <section class="bench-detail__card bench-detail__hero"
             [style.border-top-color]="firmColor(entry.firm)">
      <div class="bench-detail__hero-main">
        @if (entry.consultantId && entry.photoId) {
          <img class="bench-detail__avatar bench-detail__avatar--photo"
               [lazyImage]="entry.consultantId" [lazyImagePhotoId]="entry.photoId"
               [alt]="entry.fullName"/>
        } @else {
          <span class="bench-detail__avatar">{{ getInitials(entry.fullName) }}</span>
        }
        <div class="bench-detail__hero-identity">
          <h1 class="bench-detail__hero-name">{{ entry.fullName }}</h1>
          <div class="bench-detail__hero-tags">
            @if (entry.abbreviation) {
              <span class="bench-detail__kuerzel">{{ entry.abbreviation }}</span>
            }
            @for (category of categoriesOf(entry); track category) {
              <span class="bench-detail__type-badge"
                    [ngClass]="'bench-detail__type-badge--' + category.toLowerCase()">
                {{ ('benchOverview.type.' + category.toLowerCase()) | translate }}
              </span>
            }
            @if (entry.position) {
              <span class="bench-detail__hero-position">{{ entry.position }}</span>
            }
          </div>
        </div>
      </div>

      <div class="bench-detail__hero-right">
      <div class="bench-detail__hero-meta">
        <div class="bench-detail__meta-item">
          <span class="bench-detail__meta-label">{{ 'benchDetail.hero.firm' | translate }}</span>
          <span class="bench-detail__meta-value">
            @if (entry.firm) {
              <app-company-badge [abbreviation]="entry.firm"></app-company-badge>
            } @else {
              —
            }
          </span>
        </div>
        <div class="bench-detail__meta-item">
          <span class="bench-detail__meta-label">{{ 'benchDetail.hero.availableFrom' | translate }}</span>
          <span class="bench-detail__meta-value">{{ (entry.availableFrom | date: 'dd.MM.yyyy') || '—' }}</span>
        </div>
        @if (commitmentFrom) {
          <div class="bench-detail__meta-item">
            <span class="bench-detail__meta-label">{{ 'benchDetail.hero.commitmentFrom' | translate }}</span>
            <span class="bench-detail__meta-value">{{ commitmentFrom | date: 'dd.MM.yyyy' }}</span>
          </div>
        }
        <div class="bench-detail__meta-item">
          <span class="bench-detail__meta-label">{{ 'benchDetail.hero.location' | translate }}</span>
          <span class="bench-detail__meta-value">{{ entry.location || '—' }}</span>
        </div>
        @if (!isApplicant(entry)) {
          <div class="bench-detail__meta-item">
            <span class="bench-detail__meta-label">{{ 'benchDetail.hero.vacation' | translate }}</span>
            <span class="bench-detail__meta-value">{{ entry.remainingVacationDays ?? '—' }}</span>
          </div>
        }
        <div class="bench-detail__meta-item">
          <span class="bench-detail__meta-label">{{ 'benchDetail.hero.offers' | translate }}</span>
          <span class="bench-detail__meta-value">{{ entry.offers.length }}</span>
        </div>
      </div>

      <div class="bench-detail__hero-actions">
        @if (entry.consultantId) {
          <button class="bench-detail__profile-jump" (click)="goToProfile()">
            <mat-icon>person</mat-icon>{{ 'benchDetail.master.toProfile' | translate }}
            <mat-icon class="bench-detail__profile-jump-chevron">chevron_right</mat-icon>
          </button>
        }
        <button class="bench-detail__notes-jump" (click)="scrollToNotes()"
                [attr.aria-label]="'benchDetail.hero.notesAnchorAria' | translate: {count: entry.notes?.length ?? 0}">
          <mat-icon>sticky_note_2</mat-icon>
          {{ 'benchDetail.hero.notesAnchor' | translate: {count: entry.notes?.length ?? 0} }}
        </button>
      </div>
      </div>
    </section>

    <!-- Angebote / Anfragen -->
    <section class="bench-detail__card">
      <div class="bench-detail__section-head">
        <div class="bench-detail__section-title-group">
          <h2 class="bench-detail__section-title" #offersTitle tabindex="-1">{{ 'benchDetail.offers.title' | translate }}</h2>
          @if (showSaved && savedArea === 'offers') {
            <span class="bench-detail__saved bench-detail__offers-status">
              <mat-icon>check</mat-icon>{{ 'benchDetail.saved' | translate }}
            </span>
          }
        </div>
        <button class="bench-detail__link" [disabled]="isAddingOffer" (click)="addOffer()">
          <mat-icon>add</mat-icon>{{ 'benchDetail.offers.add' | translate }}
        </button>
      </div>

      <!-- Filter bar -->
      <div class="bench-detail__offer-filter">
        <div class="bench-detail__offer-search">
          <mat-icon>search</mat-icon>
          <input type="text" [(ngModel)]="offerSearch"
                 [placeholder]="'benchDetail.offers.searchPlaceholder' | translate">
        </div>
        @for (chip of offerFilterChips; track chip.key) {
          <button class="bench-detail__chip" [class.bench-detail__chip--active]="offerStatusFilter === chip.key"
                  (click)="offerStatusFilter = chip.key">
            {{ chip.labelKey | translate }}
            @if (chip.key !== 'ALL') {
              <span class="bench-detail__chip-count">{{ offerCount(chip.key) }}</span>
            }
          </button>
        }
        <button class="bench-detail__sort" (click)="offerNewestFirst = !offerNewestFirst">
          {{ (offerNewestFirst ? 'benchDetail.offers.newestFirst' : 'benchDetail.offers.oldestFirst') | translate }}
        </button>
      </div>

      <!-- Offer list -->
      <div class="bench-detail__offer-list">
        <div class="bench-detail__offer-head">
          <span></span>
          <span>{{ 'benchDetail.offers.col.date' | translate }}</span>
          <span>{{ 'benchDetail.offers.col.ref' | translate }}</span>
          <span>{{ 'benchDetail.offers.col.role' | translate }}</span>
          <span>{{ 'benchDetail.offers.col.customer' | translate }}</span>
          <span>{{ 'benchDetail.offers.col.intermediary' | translate }}</span>
          <span>{{ 'benchDetail.offers.col.bu' | translate }}</span>
          <span>{{ 'benchDetail.offers.col.conditions' | translate }}</span>
          <span>{{ 'benchDetail.offers.col.submittedBy' | translate }}</span>
          <span>{{ 'benchDetail.offers.col.extension' | translate }}</span>
          <span>{{ 'benchDetail.offers.col.status' | translate }}</span>
          <span></span>
        </div>

        @for (offer of visibleOffers; track offer) {
          <div class="bench-detail__offer" #offerBox [class.bench-detail__offer--expanded]="isOfferExpanded(offer)"
               [class.bench-detail__offer--invalid]="isProbabilityMissing(offer) || isOfferIncomplete(offer) || hasInvalidEstimate(offer)">
            <div class="bench-detail__offer-row" #offerRow tabindex="-1" (click)="toggleOffer(offer)">
              <mat-icon class="bench-detail__offer-chevron">
                {{ isOfferExpanded(offer) ? 'expand_more' : 'chevron_right' }}
              </mat-icon>
              <span class="bench-detail__offer-date">{{ (offer.date | date: 'dd.MM.yyyy') || '—' }}</span>
              <span class="bench-detail__offer-ref">
                <span class="bench-detail__offer-ref-internal"
                      [matTooltip]="'benchDetail.offers.field.ref' | translate">{{ offer.referenceNumber }}</span>
                @if (offer.externalReferenceNumber) {
                  <span class="bench-detail__offer-ref-external"
                        [matTooltip]="'benchDetail.offers.field.externalRef' | translate">
                    <span class="bench-detail__offer-ref-external-prefix">{{ 'benchDetail.offers.externalRefPrefix' | translate }}</span>
                    {{ offer.externalReferenceNumber }}
                  </span>
                }
              </span>
              <span class="bench-detail__offer-role">
                <span class="bench-detail__offer-role-text">{{ offer.role?.trim() || '—' }}</span>
                @if (isOfferIncomplete(offer)) {
                  <span class="bench-detail__wk-badge bench-detail__wk-badge--missing bench-detail__incomplete-badge">
                    {{ 'benchDetail.offers.incompleteBadge' | translate }}
                  </span>
                }
                @if (showSaved && savedArea === offer) {
                  <span class="bench-detail__saved bench-detail__offer-status">
                    <mat-icon>check</mat-icon>{{ 'benchDetail.saved' | translate }}
                  </span>
                } @else if (isOfferDirty(offer)) {
                  <span class="bench-detail__dirty bench-detail__offer-status">● {{ 'benchDetail.unsaved' | translate }}</span>
                }
              </span>
              <span class="bench-detail__offer-customer">{{ offer.customer || '—' }}</span>
              <span class="bench-detail__offer-intermediary">{{ offer.intermediary || '—' }}</span>
              <span class="bench-detail__offer-bu">
                <app-bu-badge [bu]="buFor(offer)"></app-bu-badge>
              </span>
              <span class="bench-detail__offer-cond">{{ conditionsLabel(offer) || '—' }}</span>
              <span class="bench-detail__offer-anbieter">
                @let sub = submitter(offer);
                <span class="bench-detail__offer-anbieter-kuerzel" [matTooltip]="sub.tooltip"
                      [matTooltipDisabled]="!sub.substituted">
                  @if (sub.substituted) {
                    <span class="bench-detail__offer-anbieter-absent">{{ sub.original }}</span>
                    <span class="bench-detail__offer-anbieter-deputy">{{ sub.active }}</span>
                  } @else {
                    {{ sub.original || '—' }}
                  }
                </span>
                @if (offer.probability != null) {
                  <span class="bench-detail__wk-badge"
                        [ngClass]="'bench-detail__wk-badge--' + offer.probability">{{ offer.probability }} %</span>
                } @else {
                  <span class="bench-detail__wk-badge bench-detail__wk-badge--missing">
                    {{ 'benchDetail.offers.probabilityMissingBadge' | translate }}
                  </span>
                }
              </span>
              <span class="bench-detail__offer-extension">
                <app-extension-badge [extension]="offer.extension" [extendedUntil]="offer.extendedUntil"></app-extension-badge>
              </span>
              <span class="bench-detail__status-cell">
                <span class="bench-detail__status-tag"
                      [ngClass]="'bench-detail__status-tag--' + offer.status?.toLowerCase()">
                  {{ ('benchOverview.status.' + offer.status?.toLowerCase()) | translate }}
                </span>
                @if (hasCommitmentDate(offer.status) && offer.commitmentFrom) {
                  <span class="bench-detail__offer-commitment">{{ offer.commitmentFrom | date: 'dd.MM.yyyy' }}</span>
                }
              </span>
              <span class="bench-detail__offer-actions">
                @if (isOfferExpanded(offer)) {
                  <button mat-icon-button class="bench-detail__offer-collapse"
                          [matTooltip]="'benchDetail.offers.collapse' | translate"
                          [attr.aria-label]="'benchDetail.offers.collapse' | translate"
                          (click)="$event.stopPropagation(); collapseOffer(offer)">
                    <mat-icon>close</mat-icon>
                  </button>
                }
                <button mat-icon-button class="bench-detail__offer-remove"
                        [disabled]="!!offer.id && (isSaving || hasBusyOffer)"
                        [matTooltip]="'benchDetail.offers.remove' | translate"
                        [attr.aria-label]="'benchDetail.offers.remove' | translate"
                        (click)="$event.stopPropagation(); askRemoveOffer(offer, offerBox)">
                  <mat-icon>delete</mat-icon>
                </button>
              </span>
            </div>

            @if (isOfferExpanded(offer)) {
              <div class="bench-detail__offer-form">
                <div class="bench-detail__grid">
                  <div class="bench-detail__field">
                    <span class="bench-detail__label">{{ 'benchDetail.offers.field.date' | translate }}</span>
                    <div class="bench-detail__date">
                      <input class="bench-detail__date-input" [matDatepicker]="offerPicker"
                             [placeholder]="'benchDetail.datePlaceholder' | translate"
                             [ngModel]="toMoment(offer.date)" (ngModelChange)="offer.date = toIso($event)">
                      <mat-datepicker-toggle class="bench-detail__date-toggle" [for]="offerPicker"></mat-datepicker-toggle>
                      <mat-datepicker #offerPicker></mat-datepicker>
                    </div>
                  </div>
                  <label class="bench-detail__field">
                    <span class="bench-detail__label">{{ 'benchDetail.offers.field.submittedBy' | translate }}</span>
                    <select class="bench-detail__input" [ngModel]="offer.submittedByUserId"
                            (ngModelChange)="setSubmitter(offer, $event)">
                      <option [ngValue]="null">—</option>
                      @for (s of submitterOptions(offer); track s.userId) {
                        <option [ngValue]="s.userId">{{ s.abbreviation }}</option>
                      }
                    </select>
                  </label>
                  <div class="bench-detail__field">
                    <span class="bench-detail__label">{{ 'benchDetail.offers.field.probability' | translate }}</span>
                    @if (isProbabilityLocked(offer) && !isProbabilityMissing(offer)) {
                      <div class="bench-detail__wk-row">
                        <span class="bench-detail__wk-value" role="status"
                              [ngClass]="'bench-detail__wk-value--' + offer.probability"
                              [attr.aria-label]="'benchDetail.offers.field.probabilityOption' | translate:{value: offer.probability}">
                          {{ offer.probability }} %
                        </span>
                        <span class="bench-detail__rate-hint">{{ 'benchDetail.offers.probabilityFromStatus' | translate }}</span>
                      </div>
                    } @else {
                      <div class="bench-detail__wk-row">
                        @if (showsProbabilityOutsideOptions(offer)) {
                          <span class="bench-detail__wk-value" role="status"
                                [ngClass]="'bench-detail__wk-value--' + offer.probability"
                                [attr.aria-label]="'benchDetail.offers.field.probabilityOption' | translate:{value: offer.probability}">
                            {{ offer.probability }} %
                          </span>
                        }
                        <div class="bench-detail__segmented bench-detail__segmented--wk"
                             [class.bench-detail__segmented--invalid]="isProbabilityMissing(offer)">
                          @for (p of probabilityOptions; track p) {
                            <button type="button" class="bench-detail__seg"
                                    [ngClass]="offer.probability === p ? 'bench-detail__seg--active-wk-' + p : ''"
                                    [attr.aria-pressed]="offer.probability === p"
                                    [attr.aria-label]="'benchDetail.offers.field.probabilityOption' | translate:{value: p}"
                                    (click)="offer.probability = p">{{ p }} %</button>
                          }
                        </div>
                      </div>
                    }
                    <div aria-live="polite">
                      @if (isProbabilityMissing(offer)) {
                        <p class="bench-detail__field-error">
                          {{ 'benchDetail.offers.probabilityMissing' | translate }}
                        </p>
                      }
                    </div>
                  </div>
                  <div class="bench-detail__field bench-detail__field--full bench-detail__ref-row">
                    <div class="bench-detail__ref-col bench-detail__ref-col--internal">
                      <span class="bench-detail__label">{{ 'benchDetail.offers.field.ref' | translate }}</span>
                      <div class="bench-detail__ref">
                        <input class="bench-detail__input bench-detail__input--readonly" type="text"
                               [value]="offer.referenceNumber" readonly>
                        <button class="bench-detail__ref-copy" type="button"
                                [matTooltip]="'benchDetail.offers.copyRef' | translate"
                                (click)="copyText(offer.referenceNumber)">
                          <mat-icon>content_copy</mat-icon>
                        </button>
                      </div>
                    </div>
                    <label class="bench-detail__ref-col bench-detail__ref-col--external">
                      <span class="bench-detail__label">{{ 'benchDetail.offers.field.externalRef' | translate }}</span>
                      <div class="bench-detail__ref">
                        <input class="bench-detail__input" type="text"
                               [(ngModel)]="offer.externalReferenceNumber" [maxlength]="LIMITS.SHORT_LABEL"
                               [placeholder]="'benchDetail.offers.externalRefPlaceholder' | translate">
                        <button class="bench-detail__ref-copy" type="button"
                                [disabled]="!offer.externalReferenceNumber"
                                [matTooltip]="'benchDetail.offers.copyRef' | translate"
                                (click)="copyText(offer.externalReferenceNumber)">
                          <mat-icon>content_copy</mat-icon>
                        </button>
                      </div>
                    </label>
                  </div>

                  <div class="bench-detail__field">
                    @let roleMissing = isOfferFieldMissing(offer, 'role');
                    <label class="bench-detail__field-control">
                      <span class="bench-detail__label">{{ 'benchDetail.offers.field.role' | translate }} *</span>
                      <input class="bench-detail__input" type="text" [maxlength]="LIMITS.SHORT_LABEL" [(ngModel)]="offer.role"
                             aria-required="true"
                             [class.bench-detail__input--error]="roleMissing"
                             [attr.aria-invalid]="roleMissing || null"
                             [attr.aria-describedby]="roleMissing ? 'offer-role-error-' + $index : null">
                    </label>
                    <div aria-live="polite">
                      @if (roleMissing) {
                        <p class="bench-detail__field-error" [id]="'offer-role-error-' + $index">
                          {{ 'benchDetail.offers.roleRequired' | translate }}
                        </p>
                      }
                    </div>
                  </div>
                  <label class="bench-detail__field">
                    <span class="bench-detail__label">{{ 'benchDetail.offers.field.customer' | translate }}</span>
                    <input class="bench-detail__input" type="text" [maxlength]="LIMITS.SHORT_LABEL" [(ngModel)]="offer.customer">
                  </label>
                  <label class="bench-detail__field">
                    <span class="bench-detail__label">{{ 'benchDetail.offers.field.intermediary' | translate }}</span>
                    <input class="bench-detail__input" type="text" [maxlength]="LIMITS.SHORT_LABEL" [(ngModel)]="offer.intermediary">
                  </label>
                  <label class="bench-detail__field">
                    <span class="bench-detail__label">{{ 'benchDetail.offers.field.bu' | translate }}</span>
                    <select class="bench-detail__input" [ngModel]="offer.businessUnitId ?? ''"
                            (ngModelChange)="offer.businessUnitId = $event || null">
                      <option value="">{{ 'benchDetail.offers.field.buPlaceholder' | translate }}</option>
                      @for (b of buOptions(offer); track b.id) {
                        <option [value]="b.id">{{ b.key }} — {{ b.name }}</option>
                      }
                    </select>
                  </label>

                  <label class="bench-detail__field">
                    <span class="bench-detail__label">{{ 'benchDetail.offers.field.status' | translate }}</span>
                    <select class="bench-detail__input" [ngModel]="offer.status"
                            (ngModelChange)="setOfferStatus(offer, $event)">
                      @for (s of statusOptions; track s) {
                        <option [value]="s">{{ ('benchOverview.status.' + s.toLowerCase()) | translate }}</option>
                      }
                    </select>
                  </label>
                  @if (!isExtensionLocked(offer) || offer.extension) {
                    <div class="bench-detail__field">
                      <span class="bench-detail__label" [id]="'offer-extension-label-' + $index">{{ 'benchDetail.offers.field.extension' | translate }}</span>
                      <div class="bench-detail__switch-field">
                        <button type="button" class="tg-switch" role="switch"
                                [class.on]="!!offer.extension"
                                [attr.aria-checked]="!!offer.extension"
                                [attr.aria-labelledby]="'offer-extension-label-' + $index"
                                [disabled]="isExtensionLocked(offer)"
                                (click)="toggleExtension(offer)">
                          <span class="tg-track">
                            <span class="tg-knob">
                              @if (offer.extension) {
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                  <path d="M20 6 9 17l-5-5"/>
                                </svg>
                              }
                            </span>
                          </span>
                        </button>
                      </div>
                    </div>
                  }
                  @if (offer.extension && !isEstimateEditable(offer)) {
                    <div class="bench-detail__field bench-detail__extended-until">
                      <span class="bench-detail__label">{{ 'benchDetail.offers.field.extendedUntil' | translate }}</span>
                      <div class="bench-detail__date" [class.bench-detail__date--disabled]="isExtendedUntilLocked(offer)">
                        <input class="bench-detail__date-input" [matDatepicker]="extensionPicker"
                               [placeholder]="'benchDetail.datePlaceholder' | translate"
                               [ngModel]="toMoment(offer.extendedUntil)" (ngModelChange)="offer.extendedUntil = toIso($event)"
                               [disabled]="isExtendedUntilLocked(offer)">
                        <mat-datepicker-toggle class="bench-detail__date-toggle" [for]="extensionPicker"
                                               [disabled]="isExtendedUntilLocked(offer)"></mat-datepicker-toggle>
                        <mat-datepicker #extensionPicker></mat-datepicker>
                      </div>
                    </div>
                  }
                  @if (hasCommitmentDate(offer.status)) {
                    <div class="bench-detail__field">
                      <span class="bench-detail__label">{{ 'benchDetail.offers.field.commitmentFrom' | translate }}</span>
                      <div class="bench-detail__date">
                        <input class="bench-detail__date-input" [matDatepicker]="zusagePicker"
                               [placeholder]="'benchDetail.datePlaceholder' | translate"
                               [ngModel]="toMoment(offer.commitmentFrom)" (ngModelChange)="offer.commitmentFrom = toIso($event)">
                        <mat-datepicker-toggle class="bench-detail__date-toggle" [for]="zusagePicker"></mat-datepicker-toggle>
                        <mat-datepicker #zusagePicker></mat-datepicker>
                      </div>
                    </div>
                  }
                  @if (isNewlyProjectCreated(offer)) {
                    <p class="bench-detail__project-created-hint bench-detail__field--full"
                       [class.bench-detail__project-created-hint--warn]="!hasLinkedProfile"
                       aria-live="polite">
                      <mat-icon class="bench-detail__project-created-hint-icon">
                        {{ hasLinkedProfile ? 'info' : 'warning_amber' }}
                      </mat-icon>
                      <span>
                        @if (hasLinkedProfile && offer.extension) {
                          {{ 'benchDetail.offers.projectCreated.hintExtension' | translate: {name: entry.fullName} }}
                        } @else if (hasLinkedProfile) {
                          {{ 'benchDetail.offers.projectCreated.hint' | translate: {name: entry.fullName} }}
                        } @else {
                          {{ 'benchDetail.offers.projectCreated.hintNoProfile' | translate }}
                        }
                      </span>
                    </p>
                  }
                  <fieldset class="bench-detail__estimate bench-detail__field--full">
                    @if (isEstimateEditable(offer)) {
                      @let estimateEndInvalid = isProjectRangeInvalid(offer);
                      @let estimateVolumeInvalid = isVolumeInvalid(offer);
                      <legend class="bench-detail__estimate-title">{{ 'benchDetail.offers.projectEstimate.title' | translate }}</legend>
                      <div class="bench-detail__estimate-grid">
                        @if (offer.extension) {
                          <div class="bench-detail__field bench-detail__extended-until">
                            <label class="bench-detail__label" [for]="'offer-extended-until-' + $index">
                              {{ 'benchDetail.offers.field.extendedUntil' | translate }}
                            </label>
                            <div class="bench-detail__date" [class.bench-detail__date--disabled]="isExtendedUntilLocked(offer)">
                              <input class="bench-detail__date-input" [id]="'offer-extended-until-' + $index"
                                     [matDatepicker]="extensionPicker"
                                     [placeholder]="'benchDetail.datePlaceholder' | translate"
                                     [ngModel]="toMoment(offer.extendedUntil)" (ngModelChange)="offer.extendedUntil = toIso($event)"
                                     [disabled]="isExtendedUntilLocked(offer)">
                              <mat-datepicker-toggle class="bench-detail__date-toggle" [for]="extensionPicker"
                                                     [disabled]="isExtendedUntilLocked(offer)"></mat-datepicker-toggle>
                              <mat-datepicker #extensionPicker></mat-datepicker>
                            </div>
                          </div>
                        } @else {
                          <div class="bench-detail__field">
                            <label class="bench-detail__label" [for]="'offer-estimate-start-' + $index">
                              {{ 'benchDetail.offers.projectCreated.field.projectStart' | translate }}
                            </label>
                            <div class="bench-detail__date">
                              <input class="bench-detail__date-input" [id]="'offer-estimate-start-' + $index"
                                     [matDatepicker]="estimateStartPicker"
                                     [placeholder]="'benchDetail.datePlaceholder' | translate"
                                     [ngModel]="toMoment(offer.projectStart ?? null)"
                                     (ngModelChange)="offer.projectStart = toIso($event)">
                              <mat-datepicker-toggle class="bench-detail__date-toggle" [for]="estimateStartPicker"></mat-datepicker-toggle>
                              <mat-datepicker #estimateStartPicker></mat-datepicker>
                            </div>
                          </div>
                          <div class="bench-detail__field">
                            <label class="bench-detail__label" [for]="'offer-estimate-end-' + $index">
                              {{ 'benchDetail.offers.projectCreated.field.projectEnd' | translate }}
                            </label>
                            <div class="bench-detail__date" [class.bench-detail__date--error]="estimateEndInvalid">
                              <input class="bench-detail__date-input" [id]="'offer-estimate-end-' + $index"
                                     [matDatepicker]="estimateEndPicker"
                                     [placeholder]="'benchDetail.datePlaceholder' | translate"
                                     [ngModel]="toMoment(offer.projectEnd ?? null)"
                                     (ngModelChange)="offer.projectEnd = toIso($event)"
                                     [attr.aria-invalid]="estimateEndInvalid || null"
                                     [attr.aria-describedby]="estimateEndInvalid ? 'offer-estimate-end-error-' + $index : null">
                              <mat-datepicker-toggle class="bench-detail__date-toggle" [for]="estimateEndPicker"></mat-datepicker-toggle>
                              <mat-datepicker #estimateEndPicker></mat-datepicker>
                            </div>
                            <div aria-live="polite">
                              @if (estimateEndInvalid) {
                                <p class="bench-detail__field-error" [id]="'offer-estimate-end-error-' + $index">
                                  {{ 'benchDetail.offers.projectCreated.validation.endBeforeStart' | translate }}
                                </p>
                              }
                            </div>
                          </div>
                        }
                        <div class="bench-detail__field">
                          <label class="bench-detail__label" [for]="'offer-estimate-volume-' + $index">
                            {{ 'benchDetail.offers.projectCreated.field.volume' | translate }}
                          </label>
                          <input class="bench-detail__input" type="number" min="0.01" max="999999.99" step="0.01"
                                 [id]="'offer-estimate-volume-' + $index"
                                 [(ngModel)]="offer.volume"
                                 [class.bench-detail__input--error]="estimateVolumeInvalid"
                                 [attr.aria-invalid]="estimateVolumeInvalid || null"
                                 [attr.aria-describedby]="estimateVolumeInvalid ? 'offer-estimate-volume-error-' + $index : null">
                          <div aria-live="polite">
                            @if (estimateVolumeInvalid) {
                              <p class="bench-detail__field-error" [id]="'offer-estimate-volume-error-' + $index">
                                {{ 'benchDetail.offers.projectCreated.validation.volumeInvalid' | translate }}
                              </p>
                            }
                          </div>
                        </div>
                        <div class="bench-detail__field">
                          <label class="bench-detail__label" [for]="'offer-estimate-unit-' + $index">
                            {{ 'benchDetail.offers.projectCreated.field.volumeUnit' | translate }}
                          </label>
                          <select class="bench-detail__input" [id]="'offer-estimate-unit-' + $index" [(ngModel)]="offer.volumeUnit">
                            <option [ngValue]="null">—</option>
                            @for (unit of volumeUnitOptions; track unit) {
                              <option [ngValue]="unit">
                                {{ ('benchDetail.offers.projectCreated.unit.' + unit.toLowerCase()) | translate }}
                              </option>
                            }
                          </select>
                        </div>
                      </div>
                      <p class="bench-detail__estimate-hint">{{ 'benchDetail.offers.projectEstimate.replaceHint' | translate }}</p>
                    } @else {
                      <legend class="bench-detail__estimate-title">{{ 'benchDetail.offers.projectEstimate.titleConfirmed' | translate }}</legend>
                      <div class="bench-detail__estimate-grid">
                        <div class="bench-detail__field">
                          <span class="bench-detail__label">{{ 'benchDetail.offers.projectCreated.field.projectStart' | translate }}</span>
                          <span class="bench-detail__estimate-value">{{ estimateDateLabel(offer.projectStart) }}</span>
                        </div>
                        <div class="bench-detail__field">
                          <span class="bench-detail__label">{{ 'benchDetail.offers.projectCreated.field.projectEnd' | translate }}</span>
                          <span class="bench-detail__estimate-value">{{ estimateDateLabel(offer.projectEnd) }}</span>
                        </div>
                        <div class="bench-detail__field">
                          <span class="bench-detail__label">{{ 'benchDetail.offers.projectCreated.field.volume' | translate }}</span>
                          <span class="bench-detail__estimate-value">{{ estimateVolumeLabel(offer) }}</span>
                        </div>
                      </div>
                    }
                  </fieldset>
                  <label class="bench-detail__field">
                    <span class="bench-detail__label">{{ 'benchDetail.offers.field.interviewPdl' | translate }}</span>
                    <select class="bench-detail__input" [(ngModel)]="offer.interviewPdl">
                      @for (opt of interviewOptions; track opt) {
                        <option [value]="opt">{{ opt || '—' }}</option>
                      }
                    </select>
                  </label>

                  <label class="bench-detail__field">
                    <span class="bench-detail__label">{{ 'benchDetail.offers.field.interviewCustomer' | translate }}</span>
                    <select class="bench-detail__input" [(ngModel)]="offer.interviewCustomer">
                      @for (opt of interviewOptions; track opt) {
                        <option [value]="opt">{{ opt || '—' }}</option>
                      }
                    </select>
                  </label>
                  <div class="bench-detail__field bench-detail__field--wide">
                    <div class="bench-detail__rates-row">
                      <div class="bench-detail__field">
                        @let remoteMissing = isOfferFieldMissing(offer, 'hourlyRateRemote');
                        <label class="bench-detail__field-control">
                          <span class="bench-detail__label">{{ 'benchDetail.offers.field.rateRemote' | translate }} *</span>
                          <div class="bench-detail__input-suffix">
                            <input class="bench-detail__input" type="number" min="0.01" step="0.01"
                                   aria-required="true"
                                   [(ngModel)]="offer.hourlyRateRemote"
                                   [class.bench-detail__input--error]="remoteMissing"
                                   [attr.aria-invalid]="remoteMissing || null"
                                   [attr.aria-describedby]="remoteMissing ? 'offer-rate-remote-error-' + $index : null">
                            <span class="bench-detail__suffix">{{ 'benchDetail.offers.ratePerHour' | translate }}</span>
                          </div>
                        </label>
                        <div aria-live="polite">
                          @if (remoteMissing) {
                            <p class="bench-detail__field-error" [id]="'offer-rate-remote-error-' + $index">
                              {{ 'benchDetail.offers.rateRequired' | translate }}
                            </p>
                          }
                        </div>
                      </div>
                      <div class="bench-detail__field">
                        @let onSiteMissing = isOfferFieldMissing(offer, 'hourlyRateOnSite');
                        <label class="bench-detail__field-control">
                          <span class="bench-detail__label">{{ 'benchDetail.offers.field.rateOnSite' | translate }} *</span>
                          <div class="bench-detail__input-suffix">
                            <input class="bench-detail__input" type="number" min="0.01" step="0.01"
                                   aria-required="true"
                                   [(ngModel)]="offer.hourlyRateOnSite"
                                   [class.bench-detail__input--error]="onSiteMissing"
                                   [attr.aria-invalid]="onSiteMissing || null"
                                   [attr.aria-describedby]="onSiteMissing ? 'offer-rate-onsite-error-' + $index : null">
                            <span class="bench-detail__suffix">{{ 'benchDetail.offers.ratePerHour' | translate }}</span>
                          </div>
                        </label>
                        <div aria-live="polite">
                          @if (onSiteMissing) {
                            <p class="bench-detail__field-error" [id]="'offer-rate-onsite-error-' + $index">
                              {{ 'benchDetail.offers.rateRequired' | translate }}
                            </p>
                          }
                        </div>
                      </div>
                    </div>

                    @if (showMissingCostHint(offer)) {
                      <p class="bench-detail__rate-hint" aria-live="polite">
                        {{ 'benchDetail.offers.missingCostHint' | translate }}
                      </p>
                    }
                  </div>

                  <div class="bench-detail__field bench-detail__field--full">
                    <span class="bench-detail__label">{{ 'benchDetail.offers.field.hubspot' | translate }}</span>
                    <div class="bench-detail__ref">
                      <input class="bench-detail__input" type="url" [maxlength]="LIMITS.URL" [(ngModel)]="offer.hubspotUrl">
                      <button class="bench-detail__ref-open" type="button"
                              [disabled]="!offer.hubspotUrl"
                              [matTooltip]="'benchDetail.offers.openHubspot' | translate"
                              (click)="openHubspot(offer.hubspotUrl)">
                        <mat-icon>chevron_right</mat-icon>
                      </button>
                    </div>
                  </div>

                  <div class="bench-detail__field bench-detail__field--full">
                    <span class="bench-detail__label">{{ 'benchDetail.offers.field.attachment' | translate }}</span>

                    @if (!canAttach(offer)) {
                      <p class="bench-detail__attachment-hint">
                        {{ 'benchDetail.offers.attachment.saveFirst' | translate }}
                      </p>
                    } @else if (offer.attachment; as attachment) {
                      <div class="bench-detail__attachment"
                           [class.bench-detail__attachment--busy]="isBusy(offer)">
                        <span class="bench-detail__attachment-badge"
                              [ngClass]="'bench-detail__attachment-badge--' + attachmentKind(offer)">
                          @if (isBusy(offer)) {
                            <mat-progress-spinner diameter="20" mode="indeterminate"></mat-progress-spinner>
                          } @else {
                            <mat-icon>{{ attachmentIcon(offer) }}</mat-icon>
                          }
                        </span>
                        <span class="bench-detail__attachment-info">
                          <button type="button" class="bench-detail__attachment-name"
                                  [disabled]="isBusy(offer)"
                                  [matTooltip]="'benchDetail.offers.attachment.download' | translate"
                                  (click)="downloadAttachment(offer)">
                            {{ attachment.fileName || ('benchDetail.offers.attachment.unnamed' | translate) }}
                          </button>
                          <span class="bench-detail__attachment-meta">
                            @if (attachmentSize(offer); as size) {
                              <span>{{ size }}</span>
                            }
                            @if (attachment.uploadedBy) {
                              <span>{{ attachment.uploadedBy }}</span>
                            }
                            @if (attachment.uploadedAt) {
                              <span>{{ attachment.uploadedAt | date: 'dd.MM.yyyy' }}</span>
                            }
                          </span>
                        </span>
                        <span class="bench-detail__attachment-actions">
                          <button mat-icon-button
                                  [disabled]="isBusy(offer)"
                                  [attr.aria-label]="'benchDetail.offers.attachment.download' | translate"
                                  [matTooltip]="'benchDetail.offers.attachment.download' | translate"
                                  (click)="downloadAttachment(offer)">
                            <mat-icon>download</mat-icon>
                          </button>
                          <input #replaceInput type="file" hidden
                                 [disabled]="isWriteLocked(offer)"
                                 (change)="onAttachmentSelected(offer, $event)">
                          <button mat-icon-button
                                  [disabled]="isWriteLocked(offer)"
                                  [attr.aria-label]="'benchDetail.offers.attachment.replace' | translate"
                                  [matTooltip]="'benchDetail.offers.attachment.replace' | translate"
                                  (click)="replaceInput.click()">
                            <mat-icon>swap_horiz</mat-icon>
                          </button>
                          <button mat-icon-button
                                  [disabled]="isWriteLocked(offer)"
                                  [attr.aria-label]="'benchDetail.offers.attachment.remove' | translate"
                                  [matTooltip]="'benchDetail.offers.attachment.remove' | translate"
                                  (click)="askRemoveAttachment(offer)">
                            <mat-icon>delete</mat-icon>
                          </button>
                        </span>
                      </div>
                      <label class="bench-detail__field bench-detail__attachment-note">
                        <span class="bench-detail__label">{{ 'benchDetail.offers.attachment.note' | translate }}</span>
                        <input class="bench-detail__input" type="text"
                               [maxlength]="LIMITS.FREETEXT"
                               [disabled]="isBusy(offer)"
                               [placeholder]="'benchDetail.offers.attachment.notePlaceholder' | translate"
                               [(ngModel)]="attachment.note">
                      </label>
                    } @else {
                      <input #addInput type="file" hidden
                             [disabled]="isWriteLocked(offer)"
                             (change)="onAttachmentSelected(offer, $event)">
                      <button type="button"
                              class="bench-detail__attachment bench-detail__attachment--empty"
                              [class.bench-detail__attachment--busy]="isBusy(offer)"
                              [disabled]="isWriteLocked(offer)"
                              (click)="addInput.click()">
                        <span class="bench-detail__attachment-badge bench-detail__attachment-badge--empty">
                          @if (isBusy(offer)) {
                            <mat-progress-spinner diameter="20" mode="indeterminate"></mat-progress-spinner>
                          } @else {
                            <mat-icon>upload_file</mat-icon>
                          }
                        </span>
                        <span class="bench-detail__attachment-info">
                          <span class="bench-detail__attachment-add">
                            {{ 'benchDetail.offers.attachment.add' | translate }}
                          </span>
                          <span class="bench-detail__attachment-meta">
                            {{ 'benchDetail.offers.attachment.hint' | translate }}
                          </span>
                        </span>
                      </button>
                    }
                  </div>

                  <div class="bench-detail__field bench-detail__field--full">
                    <div class="bench-detail__offer-notes-head">
                      <span class="bench-detail__label">{{ 'benchDetail.offers.field.notes' | translate }}</span>
                      <button type="button" class="bench-detail__link" (click)="addOfferNote(offer)">
                        <mat-icon>add</mat-icon>{{ 'benchDetail.notes.add' | translate }}
                      </button>
                    </div>
                    @if (offer.notes.length) {
                      <div class="bench-detail__notes">
                        <div class="bench-detail__notes-head">
                          <span>{{ 'benchDetail.notes.col.date' | translate }}</span>
                          <span>{{ 'benchDetail.notes.col.author' | translate }}</span>
                          <span>{{ 'benchDetail.notes.col.text' | translate }}</span>
                          <span></span>
                        </div>
                        @for (note of offer.notes; track $index) {
                          <div class="bench-detail__note-row">
                            <div class="bench-detail__date">
                              <input class="bench-detail__date-input" [matDatepicker]="offerNotePicker"
                                     [placeholder]="'benchDetail.datePlaceholder' | translate"
                                     [ngModel]="toMoment(note.date)" (ngModelChange)="note.date = toIso($event)">
                              <mat-datepicker-toggle class="bench-detail__date-toggle" [for]="offerNotePicker"></mat-datepicker-toggle>
                              <mat-datepicker #offerNotePicker></mat-datepicker>
                            </div>
                            <span class="bench-detail__note-author">{{ note.author || '—' }}</span>
                            <textarea class="bench-detail__input bench-detail__textarea" rows="1"
                                      [maxlength]="LIMITS.FREETEXT"
                                      [(ngModel)]="note.text" (keydown)="onOfferNoteKeydown($event, offer, note)"></textarea>
                            <button mat-icon-button class="bench-detail__note-remove"
                                    [matTooltip]="'benchDetail.notes.remove' | translate"
                                    [attr.aria-label]="'benchDetail.notes.remove' | translate"
                                    (click)="askRemoveOfferNote(offer, note)">
                              <mat-icon>delete</mat-icon>
                            </button>
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>
                <div class="bench-detail__section-save-row bench-detail__offer-save-row">
                  @if (offerSaveBlockedHint(offer); as blockedHint) {
                    <span class="bench-detail__offer-save-blocked">{{ blockedHint | translate }}</span>
                  }
                  <span class="bench-detail__save-tooltip"
                        [matTooltip]="'benchDetail.offers.saveHint' | translate" [matTooltipDisabled]="canSaveOffer(offer)">
                    <button mat-flat-button class="bench-detail__offer-save"
                            [disabled]="!canSaveOffer(offer)"
                            [attr.aria-label]="offerSaveName(offer)"
                            [matTooltip]="'benchDetail.offers.saveHint' | translate" [matTooltipDisabled]="!canSaveOffer(offer)"
                            (click)="saveOffer(offer, offerBox, offerRow)">
                      {{ 'benchDetail.offers.save' | translate }}
                    </button>
                  </span>
                </div>
              </div>
            }
          </div>
        } @empty {
          <p class="bench-detail__empty">{{ 'benchDetail.offers.empty' | translate }}</p>
        }
      </div>
    </section>

    <!-- Bemerkungen -->
    <section class="bench-detail__card" #notesSection>
      <div class="bench-detail__section-head">
        <div class="bench-detail__section-title-group">
          <h2 class="bench-detail__section-title" #notesTitle tabindex="-1">{{ 'benchDetail.notes.title' | translate }}</h2>
          @if (showSaved && savedArea === 'notes') {
            <span class="bench-detail__saved bench-detail__notes-status">
              <mat-icon>check</mat-icon>{{ 'benchDetail.saved' | translate }}
            </span>
          } @else if (isNotesDirty) {
            <span class="bench-detail__dirty bench-detail__notes-status">● {{ 'benchDetail.unsaved' | translate }}</span>
          }
        </div>
        <button class="bench-detail__link" [disabled]="isSaving" (click)="addNote()">
          <mat-icon>add</mat-icon>{{ 'benchDetail.notes.add' | translate }}
        </button>
      </div>
      <p class="bench-detail__notes-hint">{{ 'benchDetail.notes.hint' | translate }}</p>

      <div class="bench-detail__notes">
        <div class="bench-detail__notes-head">
          <span>{{ 'benchDetail.notes.col.date' | translate }}</span>
          <span>{{ 'benchDetail.notes.col.author' | translate }}</span>
          <span>{{ 'benchDetail.notes.col.text' | translate }}</span>
          <span></span>
        </div>
        @for (note of entry.notes; track $index) {
          <div class="bench-detail__note-row">
            <div class="bench-detail__date">
              <input class="bench-detail__date-input" [matDatepicker]="notePicker"
                     [placeholder]="'benchDetail.datePlaceholder' | translate"
                     [ngModel]="toMoment(note.date)" (ngModelChange)="note.date = toIso($event)" [disabled]="isSaving">
              <mat-datepicker-toggle class="bench-detail__date-toggle" [for]="notePicker" [disabled]="isSaving"></mat-datepicker-toggle>
              <mat-datepicker #notePicker></mat-datepicker>
            </div>
            <span class="bench-detail__note-author">{{ note.author || '—' }}</span>
            <textarea class="bench-detail__input bench-detail__textarea" rows="1"
                      [maxlength]="LIMITS.FREETEXT"
                      [(ngModel)]="note.text" [disabled]="isSaving" (keydown)="onNoteKeydown($event, note)"></textarea>
            <button mat-icon-button class="bench-detail__note-remove"
                    [matTooltip]="'benchDetail.notes.remove' | translate"
                    [attr.aria-label]="'benchDetail.notes.remove' | translate"
                    [disabled]="isSaving" (click)="askRemoveNote(note)">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        } @empty {
          <p class="bench-detail__empty">{{ 'benchDetail.notes.empty' | translate }}</p>
        }
      </div>
      <div class="bench-detail__section-save-row">
        <span class="bench-detail__save-tooltip"
              [matTooltip]="'benchDetail.notes.saveHint' | translate" [matTooltipDisabled]="canSaveNotes">
          <button mat-flat-button class="bench-detail__notes-save"
                  [disabled]="!canSaveNotes"
                  [matTooltip]="'benchDetail.notes.saveHint' | translate" [matTooltipDisabled]="!canSaveNotes"
                  (click)="saveNotes()">
            {{ 'benchDetail.notes.save' | translate }}
          </button>
        </span>
      </div>
    </section>

    <!-- Bottom action bar -->
    <div class="bench-detail__footer-actions">
      <button mat-button class="bench-detail__back" (click)="backToList()">{{ 'benchDetail.backShort' | translate }}</button>
      @if (showSaved && (savedArea === 'all' || !isDirty)) {
        <span class="bench-detail__saved"><mat-icon>check</mat-icon>{{ 'benchDetail.saved' | translate }}</span>
      } @else if (isDirty) {
        <span class="bench-detail__dirty">● {{ 'benchDetail.unsaved' | translate }}</span>
      }
      @if (hasBusyOffer) {
        <span class="bench-detail__save-blocked">
          {{ 'benchDetail.offers.attachment.saveBlocked' | translate }}
        </span>
      }
      @if (hasOfferWithoutProbability) {
        <span class="bench-detail__save-blocked">
          {{ 'benchDetail.offers.probabilityMissingSaveBlocked' | translate }}
        </span>
      }
      @if (hasIncompleteOffer) {
        <span class="bench-detail__save-blocked">
          {{ 'benchDetail.offers.incompleteSaveBlocked' | translate }}
        </span>
      }
      <span class="bench-detail__save-tooltip"
            [matTooltip]="'benchDetail.saveAllHint' | translate" [matTooltipDisabled]="!isSaveAllDisabled">
        <button mat-flat-button class="bench-detail__save"
                [disabled]="isSaveAllDisabled"
                [matTooltip]="'benchDetail.saveAllHint' | translate" [matTooltipDisabled]="isSaveAllDisabled"
                (click)="save()">
          {{ 'benchDetail.saveAll' | translate }}
        </button>
      </span>
    </div>
  </div>
}

<app-modal
  modalId="removeOffer"
  [(visible)]="removeOfferOpen"
  [title]="'benchDetail.offers.removeConfirm.title'"
  [contentTemplate]="removeOfferContent"
  [saveText]="'benchDetail.offers.removeConfirm.action'"
  [showClose]="true"
  (save)="confirmRemoveOffer()"
  (cancel)="cancelRemoveOffer()">
</app-modal>
<ng-template #removeOfferContent>
  @if (pendingOfferRemovalLabel) {
    <p>{{ 'benchDetail.offers.removeConfirm.text' | translate: {offer: pendingOfferRemovalLabel} }}</p>
  } @else {
    <p>{{ 'benchDetail.offers.removeConfirm.textUnnamed' | translate }}</p>
  }
  <p class="bench-detail__confirm-hint">
    <mat-icon class="bench-detail__confirm-hint-icon">info</mat-icon>
    <span>{{ pendingOfferRemovalHint | translate }}</span>
  </p>
</ng-template>

<app-modal
  modalId="removeNote"
  [(visible)]="removeNoteOpen"
  [title]="'benchDetail.notes.removeConfirm.title'"
  [contentTemplate]="removeNoteContent"
  [saveText]="'benchDetail.notes.removeConfirm.action'"
  [showClose]="true"
  (save)="confirmRemoveNote()"
  (cancel)="cancelRemoveNote()">
</app-modal>
<ng-template #removeNoteContent>
  @if (pendingNoteRemovalLabel) {
    <p>{{ 'benchDetail.notes.removeConfirm.text' | translate: {note: pendingNoteRemovalLabel} }}</p>
  } @else {
    <p>{{ 'benchDetail.notes.removeConfirm.textEmpty' | translate }}</p>
  }
  <p class="bench-detail__confirm-hint">
    <mat-icon class="bench-detail__confirm-hint-icon">info</mat-icon>
    <span>{{ pendingNoteRemovalHint | translate }}</span>
  </p>
</ng-template>

<app-modal
  modalId="removeAttachment"
  [(visible)]="removeAttachmentOpen"
  [title]="'benchDetail.offers.attachment.removeConfirm.title'"
  [contentTemplate]="removeAttachmentContent"
  [saveText]="'general.delete'"
  [showClose]="true"
  (save)="confirmRemoveAttachment()"
  (cancel)="cancelRemoveAttachment()">
</app-modal>
<ng-template #removeAttachmentContent>
  <p>{{ 'benchDetail.offers.attachment.removeConfirm.text' | translate }}</p>
  <p class="bench-detail__confirm-hint bench-detail__confirm-hint--warn">
    <mat-icon class="bench-detail__confirm-hint-icon">warning</mat-icon>
    <span>{{ 'benchDetail.offers.attachment.removeConfirm.hint' | translate }}</span>
  </p>
</ng-template>

<ng-template #projectCreatedConfirmContent>
  <div class="bench-detail__project-created-confirm">
    <p class="bench-detail__project-created-confirm-intro">
      {{ 'benchDetail.offers.projectCreated.confirm.intro' | translate }}
    </p>
    @if (hasLinkedProfile) {
      @for (offer of newlyProjectCreatedOffers; track offer) {
        <div class="bench-detail__project-created-offer">
          <p class="bench-detail__project-created-offer-title">
            {{ offer.referenceNumber }}{{ offer.customer ? ' — ' + offer.customer : '' }}
          </p>
          @if (offer.extension) {
            <fieldset class="bench-detail__project-extend-mode">
              <legend class="bench-detail__project-extend-legend">
                {{ 'benchDetail.offers.projectCreated.extend.mode.label' | translate }}
              </legend>
              @if (!isExtendModeFixed) {
                <label class="bench-detail__project-extend-option">
                  <input type="radio" value="extend" [name]="'extend-mode-' + $index"
                         [checked]="isExtendMode(offer)" [disabled]="isLoadingConsultantProjects"
                         (change)="setProjectCreatedMode(offer, 'extend')">
                  <span>{{ 'benchDetail.offers.projectCreated.extend.mode.extend' | translate }}</span>
                </label>
              }
              <label class="bench-detail__project-extend-option">
                <input type="radio" value="new" [name]="'extend-mode-' + $index"
                       [checked]="!isExtendMode(offer)" [disabled]="isLoadingConsultantProjects"
                       (change)="setProjectCreatedMode(offer, 'new')">
                <span>{{ 'benchDetail.offers.projectCreated.extend.mode.new' | translate }}</span>
              </label>
            </fieldset>
            @if (projectsLoadState === 'error') {
              <p class="bench-detail__project-extend-note" role="status">
                {{ 'benchDetail.offers.projectCreated.extend.loadError' | translate }}
              </p>
            } @else if (isExtendModeFixed) {
              <p class="bench-detail__project-extend-note" role="status">
                {{ 'benchDetail.offers.projectCreated.extend.noProjects' | translate }}
              </p>
            }
          }
          @if (isDialogPrefilled(offer)) {
            <p class="bench-detail__project-created-hint bench-detail__project-created-prefilled">
              <mat-icon class="bench-detail__project-created-hint-icon" aria-hidden="true">info</mat-icon>
              <span>{{ 'benchDetail.offers.projectEstimate.prefilledHint' | translate }}</span>
            </p>
          }
          <div class="bench-detail__project-created-fields">
            @if (isExtendMode(offer)) {
              @let project = selectedExtendProject(offer);
              <label class="bench-detail__field bench-detail__field--full bench-detail__project-extend-project">
                <span class="bench-detail__label">
                  {{ 'benchDetail.offers.projectCreated.extend.field.project' | translate }} *
                </span>
                @if (isLoadingConsultantProjects) {
                  <select class="bench-detail__input" disabled>
                    <option>{{ 'benchDetail.offers.projectCreated.extend.loading' | translate }}</option>
                  </select>
                } @else {
                  <select class="bench-detail__input"
                          [ngModel]="offer.extendProjectUuid ?? null" (ngModelChange)="selectExtendProject(offer, $event)">
                    <option [ngValue]="null">—</option>
                    @for (p of consultantProjects; track p.uuid) {
                      <option [ngValue]="p.uuid">
                        {{ p.kunde || '—' }} – {{ p.stelle || '—' }}
                        @if (p.ende) {
                          ({{ (p.beginn | date: 'dd.MM.yyyy') || '—' }} – {{ p.ende | date: 'dd.MM.yyyy' }})
                        } @else {
                          {{ 'benchDetail.offers.projectCreated.extend.option.openEnd'
                            | translate: {start: (p.beginn | date: 'dd.MM.yyyy') || '—'} }}
                        }
                      </option>
                    }
                  </select>
                }
              </label>

              <div class="bench-detail__field">
                <span class="bench-detail__label">
                  {{ 'benchDetail.offers.projectCreated.extend.field.projectStart' | translate }}
                </span>
                <span class="bench-detail__project-extend-value">
                  {{ (project?.beginn | date: 'dd.MM.yyyy') || '—' }}
                </span>
              </div>

              <div class="bench-detail__field">
                <span class="bench-detail__label">
                  {{ 'benchDetail.offers.projectCreated.extend.field.previousEnd' | translate }}
                </span>
                <span class="bench-detail__project-extend-value">
                  @if (!project) {
                    —
                  } @else if (project.ende) {
                    {{ project.ende | date: 'dd.MM.yyyy' }}
                  } @else {
                    {{ 'benchDetail.offers.projectCreated.extend.noEnd' | translate }}
                  }
                </span>
              </div>

              <div class="bench-detail__field bench-detail__project-extend-new-end">
                <span class="bench-detail__label">
                  {{ 'benchDetail.offers.projectCreated.extend.field.newEnd' | translate }} *
                </span>
                <div class="bench-detail__date">
                  <input class="bench-detail__date-input" [matDatepicker]="extendEndPicker"
                         [min]="extendMinDate(offer)"
                         [placeholder]="'benchDetail.datePlaceholder' | translate"
                         [ngModel]="toMoment(offer.projectEnd ?? null)"
                         (ngModelChange)="setExtendNewEnd(offer, toIso($event))">
                  <mat-datepicker-toggle class="bench-detail__date-toggle"
                                         [for]="extendEndPicker"></mat-datepicker-toggle>
                  <mat-datepicker #extendEndPicker></mat-datepicker>
                </div>
              </div>
            } @else {
              <div class="bench-detail__field">
                <span class="bench-detail__label">
                  {{ 'benchDetail.offers.projectCreated.field.projectStart' | translate }} *
                </span>
                <div class="bench-detail__date">
                  <input class="bench-detail__date-input" [matDatepicker]="projectStartPicker"
                         [placeholder]="'benchDetail.datePlaceholder' | translate"
                         [ngModel]="toMoment(offer.projectStart)" (ngModelChange)="offer.projectStart = toIso($event)">
                  <mat-datepicker-toggle class="bench-detail__date-toggle"
                                         [for]="projectStartPicker"></mat-datepicker-toggle>
                  <mat-datepicker #projectStartPicker></mat-datepicker>
                </div>
              </div>

              <div class="bench-detail__field">
                <span class="bench-detail__label">
                  {{ 'benchDetail.offers.projectCreated.field.projectEnd' | translate }} *
                </span>
                <div class="bench-detail__date">
                  <input class="bench-detail__date-input" [matDatepicker]="projectEndPicker"
                         [placeholder]="'benchDetail.datePlaceholder' | translate"
                         [ngModel]="toMoment(offer.projectEnd)" (ngModelChange)="offer.projectEnd = toIso($event)">
                  <mat-datepicker-toggle class="bench-detail__date-toggle"
                                         [for]="projectEndPicker"></mat-datepicker-toggle>
                  <mat-datepicker #projectEndPicker></mat-datepicker>
                </div>
              </div>
            }

            <label class="bench-detail__field">
              <span class="bench-detail__label">
                {{ 'benchDetail.offers.projectCreated.field.volume' | translate }} *
              </span>
              <input class="bench-detail__input" type="number" min="0.01" max="999999.99" step="0.01"
                     [(ngModel)]="offer.volume">
            </label>

            <label class="bench-detail__field">
              <span class="bench-detail__label">
                {{ 'benchDetail.offers.projectCreated.field.volumeUnit' | translate }} *
              </span>
              <select class="bench-detail__input" [(ngModel)]="offer.volumeUnit">
                <option [ngValue]="null">—</option>
                @for (unit of volumeUnitOptions; track unit) {
                  <option [ngValue]="unit">
                    {{ ('benchDetail.offers.projectCreated.unit.' + unit.toLowerCase()) | translate }}
                  </option>
                }
              </select>
            </label>
          </div>

          <div class="bench-detail__project-created-errors" aria-live="polite">
            @if (isExtendProjectMissing(offer)) {
              <p class="bench-detail__field-error">
                {{ 'benchDetail.offers.projectCreated.extend.validation.projectRequired' | translate }}
              </p>
            }
            @if (isExtendMode(offer)) {
              @if (isProjectDataMissing(offer) && !isLoadingConsultantProjects) {
                <p class="bench-detail__field-error">
                  {{ 'benchDetail.offers.projectCreated.extend.validation.required' | translate }}
                </p>
              }
              @if (isExtendEndNotAfterPrevious(offer)) {
                <p class="bench-detail__field-error">
                  {{ 'benchDetail.offers.projectCreated.extend.validation.newEndNotAfterPrevious' | translate }}
                </p>
              }
            } @else if (isProjectDataMissing(offer)) {
              <p class="bench-detail__field-error">
                {{ 'benchDetail.offers.projectCreated.validation.required' | translate }}
              </p>
            }
            @if (isProjectRangeInvalid(offer)) {
              <p class="bench-detail__field-error">
                {{ 'benchDetail.offers.projectCreated.validation.endBeforeStart' | translate }}
              </p>
            }
            @if (isVolumeInvalid(offer)) {
              <p class="bench-detail__field-error">
                {{ 'benchDetail.offers.projectCreated.validation.volumeInvalid' | translate }}
              </p>
            }
          </div>
        </div>
      }
      @if (hasExtendModeOffer) {
        <p class="bench-detail__project-created-confirm-text">
          {{ 'benchDetail.offers.projectCreated.confirm.textExtend' | translate: {name: entry?.fullName} }}
        </p>
      }
      @if (hasNewModeOffer) {
        <p class="bench-detail__project-created-confirm-text">
          {{ 'benchDetail.offers.projectCreated.confirm.text' | translate: {name: entry?.fullName} }}
        </p>
      }
    } @else {
      <ul class="bench-detail__project-created-confirm-list">
        @for (offer of newlyProjectCreatedOffers; track offer) {
          <li>{{ offer.referenceNumber }}{{ offer.customer ? ' — ' + offer.customer : '' }}</li>
        }
      </ul>
      <p class="bench-detail__project-created-confirm-text bench-detail__project-created-confirm-text--warn">
        {{ 'benchDetail.offers.projectCreated.confirm.textNoProfile' | translate }}
      </p>
    }
  </div>
</ng-template>

<ng-template #projectSyncConfirmContent>
  <div class="bench-detail__project-sync-confirm">
    <p class="bench-detail__project-sync-confirm-intro">
      {{ 'benchDetail.offers.projectSync.confirm.intro' | translate }}
    </p>
    @for (offer of projectSyncOffers; track offer) {
      <div class="bench-detail__project-sync-offer">
        <p class="bench-detail__project-sync-offer-title">
          {{ offer.referenceNumber }}{{ offer.customer ? ' — ' + offer.customer : '' }}
        </p>
        <ul class="bench-detail__project-sync-changes">
          @for (change of changedProjectFields(offer); track change.labelKey) {
            <li>
              <span class="bench-detail__project-sync-change-label">{{ change.labelKey | translate }}:</span>
              <span class="bench-detail__project-sync-change-from">{{ change.from || '—' }}</span>
              <span class="bench-detail__project-sync-change-arrow" aria-hidden="true">→</span>
              <span class="bench-detail__project-sync-change-to">{{ change.to || '—' }}</span>
            </li>
          }
        </ul>
      </div>
    }
    <p class="bench-detail__project-sync-confirm-text">
      {{ 'benchDetail.offers.projectSync.confirm.text' | translate: {name: entry?.fullName} }}
    </p>
    <div class="bench-detail__project-sync-actions">
      <button mat-raised-button [type]="'button'" (click)="cancelProjectSync()">
        {{ 'general.cancel' | translate }}
      </button>
      <button mat-raised-button [type]="'button'" (click)="confirmProjectSync(false)">
        {{ 'benchDetail.offers.projectSync.confirm.offerOnly' | translate }}
      </button>
      <button mat-flat-button color="primary" [type]="'button'" (click)="confirmProjectSync(true)">
        {{ 'benchDetail.offers.projectSync.confirm.apply' | translate }}
      </button>
    </div>
  </div>
</ng-template>

<app-modal
  modalId="projectSyncConfirm"
  [(visible)]="projectSyncConfirmOpen"
  [title]="'benchDetail.offers.projectSync.confirm.title'"
  [contentTemplate]="projectSyncConfirmContent"
  [showButtons]="false"
  [showClose]="true"
  (cancel)="cancelProjectSync()">
</app-modal>

<app-modal
  modalId="projectCreatedConfirm"
  [(visible)]="projectCreatedConfirmOpen"
  [title]="'benchDetail.offers.projectCreated.confirm.title'"
  [contentTemplate]="projectCreatedConfirmContent"
  [saveText]="'benchDetail.offers.projectCreated.confirm.save'"
  [showClose]="true"
  [disabled]="projectCreatedSaveBlocked"
  (save)="confirmProjectCreated()"
  (cancel)="cancelProjectCreated()">
</app-modal>
